import React from 'react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Search } from 'lucide-react';
import type { ApprovalWorkflow } from '../types';
import { WorkflowCard } from './WorkflowCard';
import { WorkflowStatsCards } from './WorkflowStatsCards';

interface WorkflowGridProps {
  filteredApprovals: ApprovalWorkflow[];
  allApprovals: ApprovalWorkflow[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  onViewWorkflow: (workflowId: string) => void;
  onAssignWorkflow: (workflowId: string, e: React.MouseEvent) => void;
  onDuplicateWorkflow: (workflowId: string, e: React.MouseEvent) => void;
  onDeleteWorkflow: (workflowId: string) => void;
}

export const WorkflowGrid: React.FC<WorkflowGridProps> = ({
  filteredApprovals,
  allApprovals,
  searchQuery,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onViewWorkflow,
  onAssignWorkflow,
  onDuplicateWorkflow,
  onDeleteWorkflow,
}) => (
  <>
    {/* Search and Filter Section - Compact Single Line */}
    <div className="flex items-center gap-3">
      <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search workflows..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10"
        />
      </div>
      <Select value={statusFilter} onValueChange={onStatusFilterChange}>
        <SelectTrigger className="w-[180px]">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="Active">Active</SelectItem>
          <SelectItem value="Inactive">Inactive</SelectItem>
        </SelectContent>
      </Select>
    </div>

    {/* Summary Stats - Only show when 3+ workflows */}
    {allApprovals.length >= 3 && (
      <WorkflowStatsCards filteredApprovals={filteredApprovals} allApprovals={allApprovals} />
    )}

    {/* Approval Workflows Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {filteredApprovals.length === 0 ? (
        <div className="col-span-full text-center py-12">
          <p className="text-muted-foreground text-lg">No workflows found matching your criteria</p>
          <p className="text-muted-foreground text-sm mt-2">Try adjusting your search or filter settings</p>
        </div>
      ) : (
        filteredApprovals.map((approval) => (
          <WorkflowCard
            key={approval.id}
            approval={approval}
            onView={onViewWorkflow}
            onAssign={onAssignWorkflow}
            onDuplicate={onDuplicateWorkflow}
            onDelete={onDeleteWorkflow}
          />
        ))
      )}
    </div>
  </>
);
